import React from 'react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

// ── Shared palette ────────────────────────────────────────────
export const CHART_COLORS = ['#0ea5e9','#10b981','#f59e0b','#f43f5e','#8b5cf6','#06b6d4','#84cc16','#ec4899'];

const tooltipStyle = {
  backgroundColor: 'rgba(255,255,255,0.97)',
  border: '1px solid #e2e8f0',
  borderRadius: 12,
  boxShadow: '0 10px 25px rgba(0,0,0,0.08)',
  fontSize: 13,
};

function ChartWrapper({ title, subtitle, children, height = 280 }) {
  return (
    <div className="glass-card p-5 animate-fade-in">
      {(title || subtitle) && (
        <div className="mb-4">
          {title    && <h3 className="font-display font-semibold text-slate-800 text-sm">{title}</h3>}
          {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
      )}
      <ResponsiveContainer width="100%" height={height}>
        {children}
      </ResponsiveContainer>
    </div>
  );
}

// ── Bar Chart ─────────────────────────────────────────────────
export function BarChartComponent({ title, subtitle, data, bars, xKey = 'name', height }) {
  return (
    <ChartWrapper title={title} subtitle={subtitle} height={height}>
      <BarChart data={data} barGap={4} barCategoryGap="30%">
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
        <XAxis dataKey={xKey} tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
        <YAxis tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
        <Tooltip contentStyle={tooltipStyle} cursor={{ fill: 'rgba(14,165,233,0.05)' }} />
        {bars.length > 1 && <Legend wrapperStyle={{ fontSize: 12 }} />}
        {bars.map((b, i) => (
          <Bar key={b.key} dataKey={b.key} name={b.label || b.key} fill={b.color || CHART_COLORS[i]}
            radius={[6, 6, 0, 0]} maxBarSize={48} />
        ))}
      </BarChart>
    </ChartWrapper>
  );
}

// ── Line Chart ────────────────────────────────────────────────
export function LineChartComponent({ title, subtitle, data, lines, xKey = 'name', height }) {
  return (
    <ChartWrapper title={title} subtitle={subtitle} height={height}>
      <LineChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
        <XAxis dataKey={xKey} tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
        <YAxis tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
        <Tooltip contentStyle={tooltipStyle} />
        {lines.length > 1 && <Legend wrapperStyle={{ fontSize: 12 }} />}
        {lines.map((l, i) => (
          <Line key={l.key} type="monotone" dataKey={l.key} name={l.label || l.key}
            stroke={l.color || CHART_COLORS[i]} strokeWidth={2.5} dot={{ r: 4, fill: '#fff', strokeWidth: 2 }}
            activeDot={{ r: 6 }} />
        ))}
      </LineChart>
    </ChartWrapper>
  );
}

// ── Area Chart ────────────────────────────────────────────────
export function AreaChartComponent({ title, subtitle, data, areas, xKey = 'name', height }) {
  return (
    <ChartWrapper title={title} subtitle={subtitle} height={height}>
      <AreaChart data={data}>
        <defs>
          {areas.map((a, i) => (
            <linearGradient key={a.key} id={`grad-${a.key}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%"  stopColor={a.color || CHART_COLORS[i]} stopOpacity={0.25} />
              <stop offset="95%" stopColor={a.color || CHART_COLORS[i]} stopOpacity={0.02} />
            </linearGradient>
          ))}
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
        <XAxis dataKey={xKey} tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
        <YAxis tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
        <Tooltip contentStyle={tooltipStyle} />
        {areas.length > 1 && <Legend wrapperStyle={{ fontSize: 12 }} />}
        {areas.map((a, i) => (
          <Area key={a.key} type="monotone" dataKey={a.key} name={a.label || a.key}
            stroke={a.color || CHART_COLORS[i]} strokeWidth={2.5}
            fill={`url(#grad-${a.key})`} />
        ))}
      </AreaChart>
    </ChartWrapper>
  );
}

// ── Pie / Donut Chart ─────────────────────────────────────────
const RADIAN = Math.PI / 180;
function PieLabel({ cx, cy, midAngle, innerRadius, outerRadius, percent }) {
  if (percent < 0.05) return null;
  const r = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + r * Math.cos(-midAngle * RADIAN);
  const y = cy + r * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight={600}>
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
}

export function PieChartComponent({ title, subtitle, data, nameKey = 'name', valueKey = 'value', donut = false, height = 260 }) {
  const innerRadius = donut ? 60 : 0;
  return (
    <ChartWrapper title={title} subtitle={subtitle} height={height}>
      <PieChart>
        <Pie data={data} dataKey={valueKey} nameKey={nameKey}
          cx="50%" cy="50%" innerRadius={innerRadius} outerRadius={100}
          paddingAngle={donut ? 4 : 0} labelLine={false} label={PieLabel}>
          {data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
        </Pie>
        <Tooltip contentStyle={tooltipStyle} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
      </PieChart>
    </ChartWrapper>
  );
}

// ── Horizontal Bar ────────────────────────────────────────────
export function HorizontalBarChart({ title, data, dataKey, labelKey = 'name', color = CHART_COLORS[0], height }) {
  return (
    <ChartWrapper title={title} height={height || Math.max(200, data.length * 44)}>
      <BarChart data={data} layout="vertical">
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
        <XAxis type="number" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} domain={[0, 100]} unit="%" />
        <YAxis type="category" dataKey={labelKey} tick={{ fontSize: 11, fill: '#334155' }} width={120} axisLine={false} tickLine={false} />
        <Tooltip contentStyle={tooltipStyle} formatter={v => [`${v}%`]} />
        <Bar dataKey={dataKey} fill={color} radius={[0, 6, 6, 0]} maxBarSize={22} />
      </BarChart>
    </ChartWrapper>
  );
}
